import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CommunityGamesShell from "./CommunityGamesShell";
import { fetchGames } from "./gamesApi";
import { gameTypeFor } from "./gameTypes";
import { resolveMediaUrl, handleMediaImgError } from "../Helper/mediaUrl";

/**
 * Community → Games: published games as a responsive card grid.
 */
function GamesLandingPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchGames().then((result) => {
      if (cancelled) return;
      setGames(result.games || []);
      setError(!!result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <CommunityGamesShell
      title="Games"
      lede="Relax and play with the Pentecost+ community — puzzles and more, free for members."
    >
      <div className="community-social__feed-wrap">
        <div className="community-social__feed-head">
          <h2 className="community-social__feed-title">Games</h2>
        </div>

        {loading ? (
          <ul className="games-grid" aria-busy="true" aria-label="Loading games">
            {[0, 1, 2].map((i) => (
              <li key={i} className="games-card games-card--skeleton" aria-hidden="true">
                <span className="games-card__media" />
                <span className="games-card__body">
                  <span className="games-card__skeleton-line" />
                  <span className="games-card__skeleton-line games-card__skeleton-line--short" />
                </span>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="games-panel games-panel--error" role="alert">
            <h3 className="games-panel__title">Couldn’t load games</h3>
            <p className="games-panel__body">Please check your connection and try again.</p>
            <button type="button" className="btn btn-danger" onClick={retry}>
              Retry
            </button>
          </div>
        ) : games.length === 0 ? (
          <div className="games-panel games-panel--empty">
            <h3 className="games-panel__title">No games yet</h3>
            <p className="games-panel__body">
              New games will appear here as soon as they are published. Check back soon.
            </p>
          </div>
        ) : (
          <ul className="games-grid" aria-label="Games">
            {games.map((game) => {
              const type = gameTypeFor(game.type);
              return (
                <li key={game.slug} className="games-card">
                  <Link
                    to={`/games/${encodeURIComponent(game.slug)}`}
                    className="games-card__link"
                  >
                    <span className="games-card__media">
                      {game.thumbnail_url ? (
                        <img
                          src={resolveMediaUrl(game.thumbnail_url)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={handleMediaImgError}
                        />
                      ) : (
                        <span className="games-card__media-fallback" aria-hidden="true">
                          {(game.title || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="games-card__body">
                      <span className="games-card__badges">
                        <span className="games-card__badge">
                          {game.type_label || (type && type.label) || "Game"}
                        </span>
                        {game.category ? (
                          <span className="games-card__badge games-card__badge--muted">
                            {game.category}
                          </span>
                        ) : null}
                      </span>
                      <span className="games-card__title">{game.title}</span>
                      {game.description ? (
                        <span className="games-card__summary">{game.description}</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </CommunityGamesShell>
  );
}

export default GamesLandingPage;
